// P35 (2026-09-23): páginas PÚBLICAS de términos de uso (EULA) y política de privacidad.
// Intuit las exige para dar las claves de producción de la app "Alvarez Trucking Facturacion"
// (los campos "End-user license agreement URL" y "Privacy policy URL"). Tienen que abrir SIN
// iniciar sesión: por eso viven fuera de <Protected> en App.js. Son texto fijo: no llaman a la
// API ni leen datos. Cada afirmación sobre datos está cotejada contra el código (qbo_service,
// qbo_matching, qbo_invoices, ai_service, alert_service, security.py, public/index.html): si
// cambia lo que hace la plataforma, este texto se actualiza con ella.
import { Link } from "react-router-dom";
import { Truck } from "lucide-react";

const VIGENCIA_EN = "September 23, 2026";
const VIGENCIA_ES = "23 de septiembre de 2026";
const CONTACTO_URL = "https://olimpo.tech";

function Seccion({ titulo, children }) {
  return (
    <section className="mt-6">
      <h3 className="font-heading text-base font-semibold text-foreground">{titulo}</h3>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function PaginaLegal({ titulo, testid, children }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Truck className="h-5 w-5" />
          </div>
          <div className="font-heading text-xl font-bold tracking-tight">Trébol · Álvarez Trucking</div>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">{titulo}</h1>
        <nav className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <a href="#english" className="text-primary underline-offset-4 hover:underline">English</a>
          <a href="#espanol" className="text-primary underline-offset-4 hover:underline">Español</a>
          <Link to="/terminos" className="text-primary underline-offset-4 hover:underline">Terms / Términos</Link>
          <Link to="/privacidad" className="text-primary underline-offset-4 hover:underline">Privacy / Privacidad</Link>
          <Link to="/login" className="text-primary underline-offset-4 hover:underline">Login</Link>
        </nav>
        {children}
        <p className="mt-12 border-t border-border pt-4 text-xs text-muted-foreground">
          Olimpo Web Design LLC · <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a>
        </p>
      </div>
    </div>
  );
}

export function Terminos() {
  return (
    <PaginaLegal titulo="End-User License Agreement · Términos de uso" testid="pagina-terminos">
      <article id="english" className="mt-8">
        <h2 className="font-heading text-xl font-semibold">End-User License Agreement</h2>
        <p className="mt-1 text-xs text-muted-foreground">Effective date: {VIGENCIA_EN}</p>
        <Seccion titulo="1. About the platform">
          <p>
            This platform (truckingalvarez.online, including the QuickBooks application "Alvarez Trucking
            Facturacion") is a private, internal logistics and billing system built and maintained by Olimpo
            Web Design LLC ("Olimpo") for Álvarez Trucking Inc. and its affiliated companies ("Álvarez"). It is
            not offered to the general public. Only users authorized by Álvarez may access it.
          </p>
        </Seccion>
        <Seccion titulo="2. License">
          <p>
            Authorized users receive a limited, non-exclusive, non-transferable and revocable license to use the
            platform for Álvarez's internal business purposes only. Users may not copy, resell, sublicense or
            reverse engineer the platform, or use it to access data they are not authorized to see.
          </p>
        </Seccion>
        <Seccion titulo="3. QuickBooks Online connection">
          <p>
            Connecting QuickBooks Online is optional and can only be done by an Álvarez administrator through
            Intuit's authorization screen. Once connected, the platform reads customers, products/services,
            payment terms, custom field settings, company information, invoices, customer payments, bill payments
            and purchases, and it creates invoices (and can attach their supporting documents) only when an
            authorized user explicitly requests it. The connection can be removed at any time from the
            platform's Configuration page or from the Apps section of QuickBooks; disconnecting revokes the
            access granted to the platform.
          </p>
        </Seccion>
        <Seccion titulo="4. User responsibilities">
          <p>
            Users must keep their credentials confidential, review invoices and financial information before
            issuing or relying on them, and keep the information they enter accurate. Álvarez remains
            responsible for its own accounting, tax and billing decisions.
          </p>
        </Seccion>
        <Seccion titulo="5. Third-party services">
          <p>
            QuickBooks and Intuit are trademarks of Intuit Inc. This platform is not affiliated with, sponsored
            or endorsed by Intuit. Use of QuickBooks is also governed by Intuit's own terms.
          </p>
        </Seccion>
        <Seccion titulo="6. Disclaimer and limitation of liability">
          <p>
            The platform is provided "as is". It does not provide accounting, tax or legal advice. To the extent
            permitted by law, Olimpo is not liable for indirect, incidental or consequential damages arising from
            the use of the platform.
          </p>
        </Seccion>
        <Seccion titulo="7. Changes and termination">
          <p>
            These terms may be updated; the effective date above will change when they are. Access may be
            suspended or terminated when a user is no longer authorized by Álvarez or breaches these terms.
          </p>
        </Seccion>
        <Seccion titulo="8. Contact">
          <p>
            Olimpo Web Design LLC — <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a>
          </p>
        </Seccion>
      </article>

      <article id="espanol" className="mt-12 border-t border-border pt-8">
        <h2 className="font-heading text-xl font-semibold">Términos de uso (Licencia de usuario final)</h2>
        <p className="mt-1 text-xs text-muted-foreground">Vigentes desde: {VIGENCIA_ES}</p>
        <Seccion titulo="1. Sobre la plataforma">
          <p>
            Esta plataforma (truckingalvarez.online, incluida la aplicación de QuickBooks "Alvarez Trucking
            Facturacion") es un sistema privado e interno de logística y facturación, desarrollado y mantenido
            por Olimpo Web Design LLC ("Olimpo") para Álvarez Trucking Inc. y sus empresas relacionadas
            ("Álvarez"). No se ofrece al público en general. Solo pueden usarla las personas que Álvarez autorice.
          </p>
        </Seccion>
        <Seccion titulo="2. Licencia">
          <p>
            Los usuarios autorizados reciben una licencia limitada, no exclusiva, intransferible y revocable
            para usar la plataforma únicamente con fines internos de Álvarez. No está permitido copiarla,
            revenderla, sublicenciarla, hacerle ingeniería inversa ni usarla para acceder a datos que el usuario
            no esté autorizado a ver.
          </p>
        </Seccion>
        <Seccion titulo="3. Conexión con QuickBooks Online">
          <p>
            Conectar QuickBooks Online es opcional y solo puede hacerlo un administrador de Álvarez desde la
            pantalla de autorización de Intuit. Una vez conectada, la plataforma lee clientes, productos y
            servicios, términos de pago, la configuración de campos personalizados, los datos de la empresa,
            facturas, cobros de clientes, pagos a proveedores y compras, y crea facturas (y puede adjuntar sus
            documentos de respaldo) solo cuando un usuario autorizado lo pide expresamente. La conexión se puede
            quitar en cualquier momento desde la página de Configuración de la plataforma o desde la sección de
            Apps de QuickBooks; al desconectar se revoca el acceso otorgado a la plataforma.
          </p>
        </Seccion>
        <Seccion titulo="4. Responsabilidades del usuario">
          <p>
            Cada usuario debe mantener sus credenciales en reserva, revisar las facturas y la información
            financiera antes de emitirlas o apoyarse en ellas, y cargar información correcta. Álvarez sigue
            siendo responsable de sus propias decisiones contables, fiscales y de facturación.
          </p>
        </Seccion>
        <Seccion titulo="5. Servicios de terceros">
          <p>
            QuickBooks e Intuit son marcas de Intuit Inc. Esta plataforma no está afiliada, patrocinada ni
            respaldada por Intuit. El uso de QuickBooks se rige además por los términos propios de Intuit.
          </p>
        </Seccion>
        <Seccion titulo="6. Exención y limitación de responsabilidad">
          <p>
            La plataforma se ofrece "tal cual". No brinda asesoramiento contable, fiscal ni legal. En la medida
            en que la ley lo permita, Olimpo no responde por daños indirectos, incidentales o consecuentes
            derivados del uso de la plataforma.
          </p>
        </Seccion>
        <Seccion titulo="7. Cambios y terminación">
          <p>
            Estos términos pueden actualizarse; cuando eso pase, cambia la fecha de vigencia de arriba. El
            acceso puede suspenderse o darse de baja cuando un usuario deja de estar autorizado por Álvarez o
            incumple estos términos.
          </p>
        </Seccion>
        <Seccion titulo="8. Contacto">
          <p>
            Olimpo Web Design LLC — <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a>
          </p>
        </Seccion>
      </article>
    </PaginaLegal>
  );
}

export function Privacidad() {
  return (
    <PaginaLegal titulo="Privacy Policy · Política de privacidad" testid="pagina-privacidad">
      <article id="english" className="mt-8">
        <h2 className="font-heading text-xl font-semibold">Privacy Policy</h2>
        <p className="mt-1 text-xs text-muted-foreground">Effective date: {VIGENCIA_EN}</p>
        <Seccion titulo="1. Who we are">
          <p>
            This platform (truckingalvarez.online, including the QuickBooks application "Alvarez Trucking
            Facturacion") is operated by Olimpo Web Design LLC ("Olimpo") on behalf of Álvarez Trucking Inc. and
            its affiliated companies ("Álvarez"). It is a private system used only by people authorized by Álvarez.
          </p>
        </Seccion>
        <Seccion titulo="2. Information we process">
          <ul className="list-disc space-y-1 pl-5">
            <li>Account data of platform users: name, email address, role and an audit log of actions taken.</li>
            <li>Operational data entered by Álvarez: loads, clients, carriers, rates, costs and uploaded documents.</li>
            <li>
              QuickBooks Online data, only if an administrator connects QuickBooks: customers, products/services,
              payment terms, custom field settings, company information, invoices, customer payments, bill
              payments and purchases.
            </li>
            <li>Technical data: session cookies used to keep users signed in, and basic usage analytics.</li>
          </ul>
        </Seccion>
        <Seccion titulo="3. How we use it">
          <p>
            Only to run the platform for Álvarez: issuing and tracking invoices, matching customer payments and
            carrier payments, reports and payment alerts. We do not sell data, we do not use it for advertising,
            and QuickBooks data is not sent to artificial-intelligence services.
          </p>
        </Seccion>
        <Seccion titulo="4. Service providers">
          <p>
            To operate, the platform relies on: cloud hosting and database services; Intuit (QuickBooks Online),
            only when connected; email (Resend) and WhatsApp (Twilio) delivery for alerts; an AI model provider
            (Google Gemini) to read documents that users upload; and PostHog for usage analytics. They process data
            only to provide their service to the platform.
          </p>
        </Seccion>
        <Seccion titulo="5. Security">
          <p>
            Traffic is encrypted with HTTPS. QuickBooks access tokens are stored encrypted. Access is limited by
            user role, sessions use secure HTTP-only cookies, and sensitive changes are recorded in an audit log.
          </p>
        </Seccion>
        <Seccion titulo="6. Retention and disconnection">
          <p>
            Data is kept while Álvarez uses the platform. When QuickBooks is disconnected from the platform, it revokes
            its access with Intuit and deletes the stored access tokens; records already synchronized remain part
            of Álvarez's business records unless Álvarez asks for their deletion.
          </p>
        </Seccion>
        <Seccion titulo="7. Your requests">
          <p>
            To access, correct or delete information, or for any privacy question, contact Olimpo Web Design LLC
            at <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a> or the Álvarez administrator of
            your account.
          </p>
        </Seccion>
        <Seccion titulo="8. Changes">
          <p>This policy may be updated; the effective date above will change when it is.</p>
        </Seccion>
      </article>

      <article id="espanol" className="mt-12 border-t border-border pt-8">
        <h2 className="font-heading text-xl font-semibold">Política de privacidad</h2>
        <p className="mt-1 text-xs text-muted-foreground">Vigente desde: {VIGENCIA_ES}</p>
        <Seccion titulo="1. Quiénes somos">
          <p>
            Esta plataforma (truckingalvarez.online, incluida la aplicación de QuickBooks "Alvarez Trucking
            Facturacion") la opera Olimpo Web Design LLC ("Olimpo") por cuenta de Álvarez Trucking Inc. y sus
            empresas relacionadas ("Álvarez"). Es un sistema privado, que solo usan personas autorizadas por Álvarez.
          </p>
        </Seccion>
        <Seccion titulo="2. Qué información tratamos">
          <ul className="list-disc space-y-1 pl-5">
            <li>Datos de cuenta de los usuarios: nombre, correo, rol y un registro de auditoría de sus acciones.</li>
            <li>Datos operativos que carga Álvarez: loads, clientes, transportistas, tarifas, costos y documentos subidos.</li>
            <li>
              Datos de QuickBooks Online, solo si un administrador conecta QuickBooks: clientes, productos y
              servicios, términos de pago, configuración de campos personalizados, datos de la empresa, facturas,
              cobros de clientes, pagos a proveedores y compras.
            </li>
            <li>Datos técnicos: cookies de sesión para mantener la sesión iniciada y analítica básica de uso.</li>
          </ul>
        </Seccion>
        <Seccion titulo="3. Para qué la usamos">
          <p>
            Solo para hacer funcionar la plataforma para Álvarez: emitir y seguir facturas, conciliar cobros de
            clientes y pagos a transportistas, reportes y alertas de cobranza. No vendemos datos, no los usamos
            para publicidad y los datos de QuickBooks no se envían a servicios de inteligencia artificial.
          </p>
        </Seccion>
        <Seccion titulo="4. Proveedores de servicio">
          <p>
            Para funcionar, la plataforma usa: servicios de hosting y base de datos en la nube; Intuit (QuickBooks
            Online), solo si está conectado; envío de correos (Resend) y de WhatsApp (Twilio) para las alertas; un
            proveedor de modelos de IA (Google Gemini) para leer los documentos que suben los usuarios; y PostHog
            para analítica de uso. Tratan los datos solo para prestarle su servicio a la plataforma.
          </p>
        </Seccion>
        <Seccion titulo="5. Seguridad">
          <p>
            El tráfico va cifrado con HTTPS. Los tokens de acceso a QuickBooks se guardan cifrados. El acceso se
            limita según el rol de cada usuario, las sesiones usan cookies seguras HTTP-only y los cambios
            sensibles quedan en un registro de auditoría.
          </p>
        </Seccion>
        <Seccion titulo="6. Conservación y desconexión">
          <p>
            Los datos se conservan mientras Álvarez use la plataforma. Al desconectar QuickBooks desde la plataforma, esta
            revoca su acceso ante Intuit y borra los tokens de acceso guardados; los registros ya sincronizados
            siguen formando parte de los registros comerciales de Álvarez, salvo que Álvarez pida eliminarlos.
          </p>
        </Seccion>
        <Seccion titulo="7. Solicitudes">
          <p>
            Para acceder, corregir o eliminar información, o por cualquier consulta de privacidad, puede
            comunicarse con Olimpo Web Design LLC en <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a>{" "}
            o con el administrador de su cuenta en Álvarez.
          </p>
        </Seccion>
        <Seccion titulo="8. Cambios">
          <p>Esta política puede actualizarse; cuando eso pase, cambia la fecha de vigencia de arriba.</p>
        </Seccion>
      </article>
    </PaginaLegal>
  );
}
